import type { CSSProperties } from "react"
import { useTheme } from "next-themes"
import { Toaster as Sonner, toast } from "sonner"
import { useIsTouchSurfaceActive } from "@/hooks/useTouchSurface"

type ToasterProps = React.ComponentProps<typeof Sonner>

// Touch surface toast (docs/design/volunteer-mobile/README.md 2.4): foreground
// background, background text, radius 8, near the top of the screen (64px,
// just below the 56px app header, plus the safe-area inset the header pads
// by), 2.4s. Sonner's own palette variables are overridden inline on the
// toaster element, so they win over its theme rules; the typed and rich-colour
// variants (success, info, warning, error) get the same pill, so every toast
// type looks alike on the touch surface.
const TOUCH_TOAST_BG = "hsl(var(--foreground))"
const TOUCH_TOAST_TEXT = "hsl(var(--background))"
const TOUCH_TOAST_STYLE = {
  "--border-radius": "8px",
  ...Object.fromEntries(
    ["normal", "success", "info", "warning", "error"].flatMap((kind) => [
      [`--${kind}-bg`, TOUCH_TOAST_BG],
      [`--${kind}-text`, TOUCH_TOAST_TEXT],
      [`--${kind}-border`, TOUCH_TOAST_BG],
    ]),
  ),
} as CSSProperties

const TOUCH_TOAST_OFFSET = { top: "calc(64px + env(safe-area-inset-top))" }

const Toaster = ({ ...props }: ToasterProps) => {
  const { theme = "system" } = useTheme()
  const touchSurface = useIsTouchSurfaceActive()

  if (touchSurface) {
    return (
      <Sonner
        theme={theme as ToasterProps["theme"]}
        position="top-center"
        offset={TOUCH_TOAST_OFFSET}
        mobileOffset={TOUCH_TOAST_OFFSET}
        duration={2400}
        className="toaster group"
        style={TOUCH_TOAST_STYLE}
        toastOptions={{
          classNames: {
            toast:
              "group toast group-[.toaster]:bg-foreground group-[.toaster]:text-background group-[.toaster]:border-foreground",
            description: "group-[.toast]:text-background/80",
            actionButton:
              "group-[.toast]:bg-background group-[.toast]:text-foreground",
            cancelButton:
              "group-[.toast]:bg-background/20 group-[.toast]:text-background",
          },
        }}
        {...props}
      />
    )
  }

  return (
    <Sonner
      theme={theme as ToasterProps["theme"]}
      // Bottom-center: the default bottom-right overlapped action buttons and
      // form fields on the right edge of pages. Callers can still override via props.
      position="bottom-center"
      className="toaster group"
      toastOptions={{
        classNames: {
          toast:
            "group toast group-[.toaster]:bg-background group-[.toaster]:text-foreground group-[.toaster]:border-border group-[.toaster]:shadow-lg",
          description: "group-[.toast]:text-muted-foreground",
          actionButton:
            "group-[.toast]:bg-primary group-[.toast]:text-primary-foreground",
          cancelButton:
            "group-[.toast]:bg-muted group-[.toast]:text-muted-foreground",
        },
      }}
      {...props}
    />
  )
}

export { Toaster, toast }
